import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Download, Search, RefreshCw, X, Printer, CheckCircle, AlertTriangle, Eye } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

// ─── Constants ────────────────────────────────────────────────────────────────
const JURISDICTIONS = {
  CI: {
    label: 'Côte d\'Ivoire',
    flag: '🇨🇮',
    forms: [
      { id: 'attestation_revenus', name: 'Attestation de Revenus', code: 'ATT-REV-CI', description: 'Attestation annuelle des revenus de commission immobilière' },
      { id: 'its_form', name: 'Formulaire ITS', code: 'ITS-CI', description: 'Impôt sur Traitements et Salaires — Retenue à la source' },
    ],
    taxRate: 0.10,
    currency: 'XOF',
    nifFormat: 'CI-XXXX-XXXXXXXX',
    authority: 'Direction Générale des Impôts (DGI) — Côte d\'Ivoire',
  },
  US: {
    label: 'États-Unis',
    flag: '🇺🇸',
    forms: [
      { id: 'w8ben', name: 'Formulaire W-8BEN', code: 'W-8BEN', description: 'Certificate of Foreign Status of Beneficial Owner for US Tax Withholding' },
      { id: 'w9', name: 'Formulaire W-9', code: 'W-9', description: 'Request for Taxpayer Identification Number and Certification' },
    ],
    taxRate: 0.30,
    currency: 'USD',
    nifFormat: 'XXX-XX-XXXX (SSN) ou XX-XXXXXXX (EIN)',
    authority: 'Internal Revenue Service (IRS) — United States',
  },
  EU: {
    label: 'Union Européenne',
    flag: '🇪🇺',
    forms: [
      { id: 'form_2042', name: 'Formulaire 2042', code: '2042-FR', description: 'Déclaration des revenus de source étrangère — France' },
      { id: 'attestation_eu', name: 'Attestation Fiscale EU', code: 'ATT-EU', description: 'Attestation de retenue à la source pour résidents UE' },
    ],
    taxRate: 0.20,
    currency: 'EUR',
    nifFormat: 'FR XX XXX XXX XXX (NIF France)',
    authority: 'Direction Générale des Finances Publiques (DGFiP) — France / Autorité fiscale UE',
  },
};

const ROLE_LABELS = { agent: 'Agent Immobilier', notary: 'Notaire', seller: 'Vendeur' };

const formatAmount = (v, currency = 'XOF') => {
  if (!v && v !== 0) return '—';
  const opts = currency === 'XOF'
    ? { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }
    : { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return new Intl.NumberFormat('fr-FR', opts)?.format(v);
};

const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

// ─── Tax Form Preview Modal ───────────────────────────────────────────────────
const TaxFormPreviewModal = ({ formData, onClose, onDownload, onPrint }) => {
  if (!formData) return null;
  const { recipient, jurisdiction, form, transactions, summary } = formData;
  const jInfo = JURISDICTIONS?.[jurisdiction];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{form?.name}</h3>
              <p className="text-sm text-gray-500">{jInfo?.flag} {jInfo?.label} — {form?.code}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onPrint} className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              <Printer className="w-4 h-4" /> Imprimer
            </button>
            <button onClick={onDownload} className="flex items-center gap-1.5 px-3 py-2 text-sm text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors">
              <Download className="w-4 h-4" /> Télécharger
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-1"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-6" id="tax-form-print-area">
          {/* Official Header */}
          <div className="border-2 border-gray-800 rounded-xl p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">{form?.name}</h2>
                <p className="text-sm text-gray-600 mt-1">{form?.code} — {new Date()?.getFullYear()}</p>
                <p className="text-xs text-gray-500 mt-1">{jInfo?.authority}</p>
              </div>
              <div className="text-right">
                <div className="text-3xl mb-1">{jInfo?.flag}</div>
                <p className="text-xs font-semibold text-gray-600">KapitalStates SARL</p>
                <p className="text-xs text-gray-500">Abidjan, Côte d'Ivoire</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 italic">{form?.description}</p>
            </div>
          </div>

          {/* Recipient Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <h4 className="text-sm font-bold text-blue-800 mb-3 uppercase tracking-wide">Informations du Bénéficiaire</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-blue-600 font-semibold">Nom complet</p>
                <p className="text-sm font-medium text-gray-900 mt-0.5">{recipient?.name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-blue-600 font-semibold">Rôle</p>
                <p className="text-sm font-medium text-gray-900 mt-0.5">{ROLE_LABELS?.[recipient?.role] || recipient?.role || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-blue-600 font-semibold">Numéro d'identification fiscale (NIF)</p>
                <p className="text-sm font-mono font-bold text-gray-900 mt-0.5">{recipient?.nif || <span className="text-red-500 font-normal">Non renseigné</span>}</p>
              </div>
              <div>
                <p className="text-xs text-blue-600 font-semibold">Juridiction fiscale</p>
                <p className="text-sm font-medium text-gray-900 mt-0.5">{jInfo?.label}</p>
              </div>
              <div>
                <p className="text-xs text-blue-600 font-semibold">Email</p>
                <p className="text-sm text-gray-900 mt-0.5">{recipient?.email || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-blue-600 font-semibold">Période fiscale</p>
                <p className="text-sm font-medium text-gray-900 mt-0.5">{summary?.period || `Exercice ${new Date()?.getFullYear()}`}</p>
              </div>
            </div>
          </div>

          {/* Transaction Summary */}
          <div>
            <h4 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wide">Récapitulatif des Transactions</h4>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Référence</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Propriété</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Montant Brut</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Retenue ({(jInfo?.taxRate * 100)?.toFixed(0)}%)</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transactions?.length > 0 ? transactions?.map((tx, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{tx?.reference || `TXN-${String(i + 1)?.padStart(4, '0')}`}</td>
                      <td className="px-4 py-3 text-gray-700">{formatDate(tx?.date)}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-[160px] truncate">{tx?.property || '—'}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">{formatAmount(tx?.gross, jInfo?.currency)}</td>
                      <td className="px-4 py-3 text-right text-red-600 font-medium">- {formatAmount(tx?.withholding, jInfo?.currency)}</td>
                      <td className="px-4 py-3 text-right font-bold text-green-700">{formatAmount(tx?.net, jInfo?.currency)}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">Aucune transaction pour cette période</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Withholding Summary */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <h4 className="text-sm font-bold text-amber-800 mb-4 uppercase tracking-wide">Résumé des Retenues Fiscales</h4>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-lg p-4 border border-amber-100">
                <p className="text-xs text-amber-600 font-semibold mb-1">Total Revenus Bruts</p>
                <p className="text-xl font-bold text-gray-900">{formatAmount(summary?.totalGross, jInfo?.currency)}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-amber-100">
                <p className="text-xs text-amber-600 font-semibold mb-1">Total Retenues ({(jInfo?.taxRate * 100)?.toFixed(0)}%)</p>
                <p className="text-xl font-bold text-red-600">- {formatAmount(summary?.totalWithholding, jInfo?.currency)}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-amber-100">
                <p className="text-xs text-amber-600 font-semibold mb-1">Total Net Versé</p>
                <p className="text-xl font-bold text-green-700">{formatAmount(summary?.totalNet, jInfo?.currency)}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-amber-100">
                <p className="text-xs text-amber-600 font-semibold mb-1">Nombre de Transactions</p>
                <p className="text-xl font-bold text-gray-900">{summary?.count || 0}</p>
              </div>
            </div>
            {recipient?.nif && (
              <div className="bg-white rounded-lg p-3 border border-amber-100 flex items-center gap-3">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                <p className="text-xs text-gray-700">NIF enregistré: <span className="font-mono font-bold">{recipient?.nif}</span> — Formulaire conforme pour dépôt auprès de {jInfo?.authority}</p>
              </div>
            )}
          </div>

          {/* Certification */}
          <div className="border-t border-gray-200 pt-5">
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-xs text-gray-500 mb-4">Signature du bénéficiaire</p>
                <div className="border-b border-gray-400 h-12"></div>
                <p className="text-xs text-gray-500 mt-2">{recipient?.name || '___________________'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-4">Cachet et signature KapitalStates</p>
                <div className="border-b border-gray-400 h-12"></div>
                <p className="text-xs text-gray-500 mt-2">KapitalStates SARL — {formatDate(new Date()?.toISOString())}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Generate Form Modal ──────────────────────────────────────────────────────
const GenerateFormModal = ({ recipient, onClose, onGenerate }) => {
  const [jurisdiction, setJurisdiction] = useState('CI');
  const [selectedForm, setSelectedForm] = useState(JURISDICTIONS?.CI?.forms?.[0]);
  const [nif, setNif] = useState(recipient?.nif || '');
  const [period, setPeriod] = useState(`Exercice ${new Date()?.getFullYear()}`);
  const [generating, setGenerating] = useState(false);

  const jInfo = JURISDICTIONS?.[jurisdiction];

  const handleJurisdictionChange = (j) => {
    setJurisdiction(j);
    setSelectedForm(JURISDICTIONS?.[j]?.forms?.[0]);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    await new Promise(r => setTimeout(r, 800));
    onGenerate({ jurisdiction, form: selectedForm, nif, period });
    setGenerating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Générer un Formulaire Fiscal</h3>
            <p className="text-sm text-gray-500">{recipient?.name} — {ROLE_LABELS?.[recipient?.role]}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Jurisdiction */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Juridiction fiscale</label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(JURISDICTIONS)?.map(([code, info]) => (
                <button
                  key={code}
                  onClick={() => handleJurisdictionChange(code)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${jurisdiction === code ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <div className="text-2xl mb-1">{info?.flag}</div>
                  <p className="text-xs font-semibold text-gray-800">{code}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Taux: {(info?.taxRate * 100)?.toFixed(0)}%</p>
                </button>
              ))}
            </div>
          </div>

          {/* Form Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Type de formulaire</label>
            <div className="space-y-2">
              {jInfo?.forms?.map(f => (
                <button
                  key={f?.id}
                  onClick={() => setSelectedForm(f)}
                  className={`w-full p-3 rounded-xl border-2 text-left transition-all ${selectedForm?.id === f?.id ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-800">{f?.name}</p>
                    <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{f?.code}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{f?.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* NIF */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Numéro d'identification fiscale (NIF)</label>
            <p className="text-xs text-gray-400 mb-2">Format: {jInfo?.nifFormat}</p>
            <input
              type="text"
              value={nif}
              onChange={e => setNif(e?.target?.value)}
              placeholder={jInfo?.nifFormat}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>

          {/* Period */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Période fiscale</label>
            <input
              type="text"
              value={period}
              onChange={e => setPeriod(e?.target?.value)}
              placeholder={`Exercice ${new Date()?.getFullYear()}`}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>

          {/* Authority info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <p className="text-xs text-blue-600 font-semibold mb-1">Autorité fiscale compétente</p>
            <p className="text-xs text-blue-800">{jInfo?.authority}</p>
          </div>

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              Annuler
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
            >
              {generating ? <><RefreshCw className="w-4 h-4 animate-spin" /> Génération...</> : <><FileText className="w-4 h-4" /> Générer</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TaxFormGenerator = () => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterJurisdiction, setFilterJurisdiction] = useState('all');
  const [generateModal, setGenerateModal] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [generatedForms, setGeneratedForms] = useState([]);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchRecipients = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('commission_payouts')
        ?.select('id, recipient_name, recipient_role, recipient_id, commission_amount, tax_country, tax_id_number, tax_withholding_amount, tax_rate_applied, net_after_tax, payout_currency, status, created_at, escrow_transaction_id')
        ?.order('created_at', { ascending: false })
        ?.limit(200);

      if (error) throw error;

      // Group by recipient
      const grouped = {};
      (data || [])?.forEach(row => {
        const key = row?.recipient_id || row?.recipient_name;
        if (!grouped?.[key]) {
          grouped[key] = {
            id: key,
            name: row?.recipient_name || '—',
            role: row?.recipient_role || 'agent',
            nif: row?.tax_id_number || '',
            jurisdiction: row?.tax_country || 'CI',
            email: '',
            transactions: [],
            totalGross: 0,
            totalWithholding: 0,
            totalNet: 0,
          };
        }
        grouped?.[key]?.transactions?.push({
          reference: row?.id?.slice(0, 8)?.toUpperCase(),
          date: row?.created_at,
          property: row?.escrow_transaction_id ? `Séquestre #${row?.escrow_transaction_id?.slice(0, 6)}` : 'Transaction immobilière',
          gross: row?.commission_amount || 0,
          withholding: row?.tax_withholding_amount || 0,
          net: row?.net_after_tax || row?.commission_amount || 0,
          status: row?.status,
        });
        grouped[key].totalGross += row?.commission_amount || 0;
        grouped[key].totalWithholding += row?.tax_withholding_amount || 0;
        grouped[key].totalNet += row?.net_after_tax || row?.commission_amount || 0;
      });

      setRecipients(Object.values(grouped));
    } catch (err) {
      console.error('Error fetching recipients:', err);
      // Fallback mock data for demo
      setRecipients([
        { id: '1', name: 'Kouassi Jean-Baptiste', role: 'agent', nif: 'CI-2024-00123456', jurisdiction: 'CI', email: 'kb@example.com', transactions: [{ reference: 'TXN-0001', date: new Date()?.toISOString(), property: 'Villa Cocody', gross: 850000, withholding: 85000, net: 765000 }], totalGross: 850000, totalWithholding: 85000, totalNet: 765000 },
        { id: '2', name: 'Maître Diallo Fatou', role: 'notary', nif: 'CI-2024-00789012', jurisdiction: 'CI', email: 'diallo@notaire.ci', transactions: [{ reference: 'TXN-0002', date: new Date()?.toISOString(), property: 'Appartement Plateau', gross: 1200000, withholding: 120000, net: 1080000 }], totalGross: 1200000, totalWithholding: 120000, totalNet: 1080000 },
        { id: '3', name: 'Robert Thompson', role: 'agent', nif: '123-45-6789', jurisdiction: 'US', email: 'r.thompson@example.com', transactions: [{ reference: 'TXN-0003', date: new Date()?.toISOString(), property: 'Résidence Marcory', gross: 2500, withholding: 750, net: 1750 }], totalGross: 2500, totalWithholding: 750, totalNet: 1750 },
        { id: '4', name: 'Sophie Leclerc', role: 'notary', nif: 'FR 12 345 678 901', jurisdiction: 'EU', email: 's.leclerc@notaire.fr', transactions: [{ reference: 'TXN-0004', date: new Date()?.toISOString(), property: 'Duplex Bingerville', gross: 1800, withholding: 360, net: 1440 }], totalGross: 1800, totalWithholding: 360, totalNet: 1440 },
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  const filtered = recipients?.filter(r => {
    const matchSearch = !searchQuery || r?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchRole = filterRole === 'all' || r?.role === filterRole;
    const matchJurisdiction = filterJurisdiction === 'all' || r?.jurisdiction === filterJurisdiction;
    return matchSearch && matchRole && matchJurisdiction;
  });

  const handleGenerate = (recipient, { jurisdiction, form, nif, period }) => {
    const jInfo = JURISDICTIONS?.[jurisdiction];
    const formData = {
      recipient: { ...recipient, nif, jurisdiction },
      jurisdiction,
      form,
      transactions: recipient?.transactions || [],
      summary: {
        totalGross: recipient?.totalGross,
        totalWithholding: recipient?.totalWithholding || recipient?.totalGross * jInfo?.taxRate,
        totalNet: recipient?.totalNet,
        count: recipient?.transactions?.length || 0,
        period,
      },
    };
    setGenerateModal(null);
    setPreviewModal(formData);
    setGeneratedForms(prev => [{ ...formData, generatedAt: new Date()?.toISOString(), id: Date.now() }, ...prev]);
    showToast(`Formulaire ${form?.name} généré avec succès`);
  };

  const handleDownload = (formData) => {
    const jInfo = JURISDICTIONS?.[formData?.jurisdiction];
    const { recipient, form, transactions, summary } = formData;
    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>${form?.name} — ${recipient?.name}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 40px; color: #1a1a1a; }
  h1 { font-size: 20px; text-transform: uppercase; letter-spacing: 1px; }
  .header { display: flex; justify-content: space-between; border: 2px solid #1a1a1a; padding: 20px; margin-bottom: 20px; }
  .section { margin-bottom: 20px; }
  .section h2 { font-size: 12px; text-transform: uppercase; color: #666; border-bottom: 1px solid #ddd; padding-bottom: 6px; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { background: #f5f5f5; padding: 8px; text-align: left; font-size: 11px; text-transform: uppercase; }
  td { padding: 8px; border-bottom: 1px solid #eee; }
  .summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .summary-box { border: 1px solid #ddd; padding: 12px; border-radius: 6px; }
  .summary-box .label { font-size: 11px; color: #666; }
  .summary-box .value { font-size: 18px; font-weight: bold; margin-top: 4px; }
  .footer { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
  .sig-line { border-bottom: 1px solid #999; height: 40px; margin-bottom: 6px; }
  .sig-label { font-size: 11px; color: #666; }
</style>
</head>
<body>
<div class="header">
  <div>
    <h1>${form?.name}</h1>
    <p style="font-size:12px;color:#666;">${form?.code} — ${summary?.period}</p>
    <p style="font-size:11px;color:#999;">${jInfo?.authority}</p>
  </div>
  <div style="text-align:right;">
    <p style="font-size:24px;">${jInfo?.flag}</p>
    <p style="font-size:12px;font-weight:bold;">KapitalStates SARL</p>
    <p style="font-size:11px;color:#666;">Abidjan, Côte d'Ivoire</p>
  </div>
</div>
<div class="section">
  <h2>Informations du Bénéficiaire</h2>
  <table><tr><td><strong>Nom:</strong> ${recipient?.name}</td><td><strong>Rôle:</strong> ${ROLE_LABELS?.[recipient?.role] || recipient?.role}</td></tr>
  <tr><td><strong>NIF:</strong> ${recipient?.nif || 'Non renseigné'}</td><td><strong>Juridiction:</strong> ${jInfo?.label}</td></tr></table>
</div>
<div class="section">
  <h2>Transactions</h2>
  <table>
    <thead><tr><th>Référence</th><th>Date</th><th>Propriété</th><th style="text-align:right">Brut</th><th style="text-align:right">Retenue (${(jInfo?.taxRate * 100)?.toFixed(0)}%)</th><th style="text-align:right">Net</th></tr></thead>
    <tbody>${transactions?.map(tx => `<tr><td>${tx?.reference}</td><td>${formatDate(tx?.date)}</td><td>${tx?.property}</td><td style="text-align:right">${formatAmount(tx?.gross, jInfo?.currency)}</td><td style="text-align:right;color:red">-${formatAmount(tx?.withholding, jInfo?.currency)}</td><td style="text-align:right;color:green;font-weight:bold">${formatAmount(tx?.net, jInfo?.currency)}</td></tr>`)?.join('')}</tbody>
  </table>
</div>
<div class="section">
  <h2>Résumé des Retenues</h2>
  <div class="summary-grid">
    <div class="summary-box"><div class="label">Total Brut</div><div class="value">${formatAmount(summary?.totalGross, jInfo?.currency)}</div></div>
    <div class="summary-box"><div class="label">Total Retenues</div><div class="value" style="color:red">-${formatAmount(summary?.totalWithholding, jInfo?.currency)}</div></div>
    <div class="summary-box"><div class="label">Total Net Versé</div><div class="value" style="color:green">${formatAmount(summary?.totalNet, jInfo?.currency)}</div></div>
    <div class="summary-box"><div class="label">Nb Transactions</div><div class="value">${summary?.count}</div></div>
  </div>
</div>
<div class="footer">
  <div><div class="sig-line"></div><div class="sig-label">Signature du bénéficiaire — ${recipient?.name}</div></div>
  <div><div class="sig-line"></div><div class="sig-label">KapitalStates SARL — ${formatDate(new Date()?.toISOString())}</div></div>
</div>
</body></html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form?.code}_${recipient?.name?.replace(/\s+/g, '_')}_${new Date()?.getFullYear()}.html`;
    a?.click();
    URL.revokeObjectURL(url);
    showToast('Formulaire téléchargé');
  };

  const handlePrint = () => {
    const el = document.getElementById('tax-form-print-area');
    if (!el) return;
    const w = window.open('', '_blank');
    w?.document?.write(`<html><head><title>Formulaire Fiscal</title><style>body{font-family:Arial,sans-serif;margin:20px;color:#1a1a1a;} table{width:100%;border-collapse:collapse;} th,td{padding:8px;border:1px solid #ddd;font-size:12px;} th{background:#f5f5f5;} .bg-amber-50{background:#fffbeb;} .bg-blue-50{background:#eff6ff;} .border{border:1px solid #ddd;} .rounded-xl{border-radius:8px;} p{margin:4px 0;}</style></head><body>${el?.innerHTML}</body></html>`);
    w?.document?.close();
    w?.print();
  };

  const jurisdictionStats = Object.entries(JURISDICTIONS)?.map(([code, info]) => ({
    code, ...info,
    count: recipients?.filter(r => r?.jurisdiction === code)?.length,
    totalGross: recipients?.filter(r => r?.jurisdiction === code)?.reduce((s, r) => s + r?.totalGross, 0),
  }));

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${toast?.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          <CheckCircle className="w-4 h-4" />
          {toast?.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Générateur de Formulaires Fiscaux</h2>
          <p className="text-sm text-gray-500 mt-1">Génération automatique par juridiction (CI / US / EU) avec montants pré-remplis et NIF</p>
        </div>
        <button onClick={fetchRecipients} disabled={loading} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Jurisdiction Stats */}
      <div className="grid grid-cols-3 gap-4">
        {jurisdictionStats?.map(j => (
          <div key={j?.code} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">{j?.flag}</span>
              <div>
                <p className="text-sm font-bold text-gray-800">{j?.label}</p>
                <p className="text-xs text-gray-500">Taux: {(j?.taxRate * 100)?.toFixed(0)}% — {j?.forms?.length} formulaires</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Bénéficiaires</p>
                <p className="text-lg font-bold text-gray-900">{j?.count}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Total brut</p>
                <p className="text-sm font-bold text-amber-600">{formatAmount(j?.totalGross, j?.currency)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e?.target?.value)}
              placeholder="Rechercher un bénéficiaire..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>
          <select value={filterRole} onChange={e => setFilterRole(e?.target?.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent">
            <option value="all">Tous les rôles</option>
            <option value="agent">Agents</option>
            <option value="notary">Notaires</option>
            <option value="seller">Vendeurs</option>
          </select>
          <select value={filterJurisdiction} onChange={e => setFilterJurisdiction(e?.target?.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent">
            <option value="all">Toutes juridictions</option>
            <option value="CI">🇨🇮 Côte d'Ivoire</option>
            <option value="US">🇺🇸 États-Unis</option>
            <option value="EU">🇪🇺 Union Européenne</option>
          </select>
        </div>
      </div>

      {/* Recipients Table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-800">Bénéficiaires ({filtered?.length})</h3>
          <span className="text-xs text-gray-500">{generatedForms?.length} formulaire(s) généré(s) cette session</span>
        </div>
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-6 h-6 text-amber-500 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500">Chargement des données...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Bénéficiaire</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Rôle</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Juridiction</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase">NIF</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Total Brut</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Retenues</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Net</th>
                  <th className="text-center px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered?.length === 0 ? (
                  <tr><td colSpan={8} className="px-6 py-10 text-center text-gray-400">Aucun bénéficiaire trouvé</td></tr>
                ) : filtered?.map(r => {
                  const jInfo = JURISDICTIONS?.[r?.jurisdiction];
                  return (
                    <tr key={r?.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-semibold text-gray-900">{r?.name}</p>
                        <p className="text-xs text-gray-500">{r?.transactions?.length} transaction(s)</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${r?.role === 'agent' ? 'bg-blue-100 text-blue-700' : r?.role === 'notary' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}>
                          {ROLE_LABELS?.[r?.role] || r?.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="flex items-center gap-1.5 text-sm">
                          <span>{jInfo?.flag}</span>
                          <span className="font-medium text-gray-700">{r?.jurisdiction}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {r?.nif ? (
                          <span className="font-mono text-xs bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200">{r?.nif}</span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-red-500"><AlertTriangle className="w-3 h-3" /> Manquant</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-gray-900">{formatAmount(r?.totalGross, jInfo?.currency)}</td>
                      <td className="px-6 py-4 text-right text-red-600 font-medium">- {formatAmount(r?.totalWithholding, jInfo?.currency)}</td>
                      <td className="px-6 py-4 text-right font-bold text-green-700">{formatAmount(r?.totalNet, jInfo?.currency)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setGenerateModal(r)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors"
                          >
                            <FileText className="w-3.5 h-3.5" /> Générer
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Generated Forms History */}
      {generatedForms?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-800">Formulaires générés cette session ({generatedForms?.length})</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {generatedForms?.map(f => (
              <div key={f?.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{f?.form?.name} — {f?.recipient?.name}</p>
                    <p className="text-xs text-gray-500">{JURISDICTIONS?.[f?.jurisdiction]?.flag} {f?.jurisdiction} · {formatDate(f?.generatedAt)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPreviewModal(f)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                    <Eye className="w-3.5 h-3.5" /> Aperçu
                  </button>
                  <button onClick={() => handleDownload(f)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors">
                    <Download className="w-3.5 h-3.5" /> Télécharger
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      {generateModal && (
        <GenerateFormModal
          recipient={generateModal}
          onClose={() => setGenerateModal(null)}
          onGenerate={(opts) => handleGenerate(generateModal, opts)}
        />
      )}
      {previewModal && (
        <TaxFormPreviewModal
          formData={previewModal}
          onClose={() => setPreviewModal(null)}
          onDownload={() => handleDownload(previewModal)}
          onPrint={handlePrint}
        />
      )}
    </div>
  );
};

export default TaxFormGenerator;
